import { CartErrorEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { MORPH_OPTIONS } from '@theme/morph';

/**
 * Patch MORPH_OPTIONS so Horizon's cart update morph never strips `open`
 * from the cart-drawer dialog. Without this, toggling anything inside the
 * drawer (gift wrap, recommendations) causes the morph to close the dialog.
 */
(() => {
  const originalOnBeforeUpdate = MORPH_OPTIONS.onBeforeUpdate;
  MORPH_OPTIONS.onBeforeUpdate = (oldNode, newNode) => {
    if (
      oldNode instanceof HTMLDialogElement &&
      newNode instanceof HTMLDialogElement &&
      oldNode.classList.contains('cart-drawer__dialog') &&
      oldNode.hasAttribute('open') &&
      !newNode.hasAttribute('open')
    ) {
      newNode.setAttribute('open', '');
    }
    originalOnBeforeUpdate?.(oldNode, newNode);
  };
})();

const SOURCE_ID = 'cart-drawer-gift-wrap';

function syncGiftNoteState({ focus = false } = {}) {
  const giftWrapChecked = document.querySelector('.cart-drawer-gift-wrap__input:checked');
  const note = document.querySelector('.cart-drawer-note');
  if (!(note instanceof HTMLElement)) return;

  const details = note.querySelector('.cart-drawer-note__details');
  const label = note.querySelector('.cart-drawer-note__label');
  const heading = note.querySelector('[data-cart-drawer-gift-note-heading]');
  const input = note.querySelector('.cart-drawer-note__input');

  if (!(details instanceof HTMLDetailsElement)) return;

  const isGiftNote = Boolean(giftWrapChecked);
  note.classList.toggle('cart-drawer-note--gift', isGiftNote);

  if (label) {
    label.textContent = isGiftNote ? 'Agregar nota de regalo' : 'Agregar nota';
  }

  if (heading instanceof HTMLElement) {
    heading.hidden = !isGiftNote;
  }

  if (input instanceof HTMLTextAreaElement) {
    const defaultPlaceholder = input.dataset.defaultPlaceholder || 'Escribe una nota para tu pedido';
    const giftPlaceholder = input.dataset.giftPlaceholder || 'Escribe tu mensaje de regalo';
    input.placeholder = isGiftNote ? giftPlaceholder : defaultPlaceholder;
  }

  if (isGiftNote) {
    details.open = true;

    if (focus && input instanceof HTMLTextAreaElement) {
      requestAnimationFrame(() => input.focus({ preventScroll: false }));
    }
  }
}

function scheduleGiftNoteStateSync(options) {
  syncGiftNoteState(options);
  requestAnimationFrame(() => syncGiftNoteState(options));
  window.setTimeout(() => syncGiftNoteState(options), 250);
}

function getCartSectionIds() {
  const sectionIds = new Set();

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.add(component.dataset.sectionId);
    }
  });

  return Array.from(sectionIds);
}

function showCartDrawerInlineError(message) {
  const wrapper = document.querySelector('cart-drawer-component');
  if (!(wrapper instanceof HTMLElement)) return;

  const text = message || 'No pudimos agregar este producto. Elige otra opción o intenta de nuevo.';
  const target =
    wrapper.querySelector('.cart-drawer__summary') ||
    wrapper.querySelector('.cart-drawer__content') ||
    wrapper;

  let error = wrapper.querySelector('[data-cart-drawer-inline-error]');
  if (!(error instanceof HTMLElement)) {
    error = document.createElement('p');
    error.className = 'cart-drawer-inline-error';
    error.setAttribute('data-cart-drawer-inline-error', '');
    target.prepend(error);
  }

  error.textContent = text;
  error.hidden = false;
  window.clearTimeout(showCartDrawerInlineError.timeoutId);
  showCartDrawerInlineError.timeoutId = window.setTimeout(() => {
    error.hidden = true;
  }, 5000);
}

function clearCartDrawerInlineError() {
  const error = document.querySelector('[data-cart-drawer-inline-error]');
  if (error instanceof HTMLElement) error.hidden = true;
}

class CartDrawerGiftWrap extends HTMLElement {
  #activeFetch = null;

  connectedCallback() {
    this.checkbox?.addEventListener('change', this.#handleChange);
    syncGiftNoteState();
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener('change', this.#handleChange);
    this.#activeFetch?.abort();
  }

  get checkbox() {
    return this.querySelector('input[type="checkbox"]');
  }

  #handleChange = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const variantId = Number(this.dataset.variantId);
    const line = Number(this.dataset.line);

    if (!variantId) return;

    input.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.classList.toggle('is-adding', input.checked);
    this.classList.toggle('is-removing', !input.checked);
    this.classList.add('is-loading');

    const abortController = new AbortController();
    this.#activeFetch?.abort();
    this.#activeFetch = abortController;

    const sections = getCartSectionIds();
    const url = input.checked ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
    const body = input.checked
      ? JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sections.join(','),
        })
      : JSON.stringify({
          line,
          quantity: 0,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        });

    try {
      const response = await fetch(url, {
        ...fetchConfig('json', { body }),
        signal: abortController.signal,
      });
      const data = await response.json();

      if (data.status || data.errors) {
        input.checked = !input.checked;
      document.dispatchEvent(
        new CartErrorEvent(SOURCE_ID, data.message || 'Cart error', data.description, data.errors)
      );
        showCartDrawerInlineError(data.description || data.message);
        return;
      }

      preserveDrawerOpenAcrossMorph();
      // POST /cart/add.js (check) returns only the added line, with no top-level
      // item_count, so increment by 1 via the product-form source. The uncheck path
      // hits cart_change_url, which returns the full cart with a valid item_count.
      const shouldOpenGiftNote = input.checked;

      document.dispatchEvent(
        input.checked
          ? new CartUpdateEvent(data, SOURCE_ID, {
              source: 'product-form-component',
              itemCount: 1,
              sections: data.sections,
            })
          : new CartUpdateEvent(data, SOURCE_ID, {
              source: SOURCE_ID,
              itemCount: data.item_count,
              sections: data.sections,
            })
      );

      scheduleGiftNoteStateSync({ focus: shouldOpenGiftNote });
    } catch (error) {
      input.checked = !input.checked;
      showCartDrawerInlineError();
      console.error(error);
    } finally {
      this.#activeFetch = null;
      input.disabled = false;
      this.removeAttribute('aria-busy');
      this.classList.remove('is-adding', 'is-removing');
      this.classList.remove('is-loading');
    }
  };
}

if (!customElements.get('cart-drawer-gift-wrap')) {
  customElements.define('cart-drawer-gift-wrap', CartDrawerGiftWrap);
}

const QUICK_ADD_SOURCE_ID = 'cart-drawer-quick-add';

/**
 * Horizon morphs the whole header section on cart update, which strips the
 * dialog's `open` attribute and kicks it out of the top layer. Catch the
 * attribute removal and re-open via showModal() so modal state (top layer,
 * backdrop) is preserved — no PDP flash.
 */
function preserveDrawerOpenAcrossMorph() {
  const wrapper = document.querySelector('cart-drawer-component');
  if (!wrapper) return;
  const drawerDialog = wrapper.querySelector('.cart-drawer__dialog');
  if (!(drawerDialog instanceof HTMLDialogElement) || !drawerDialog.open) return;

  const isModalDialog = (dialog) => {
    try {
      return dialog.matches(':modal');
    } catch {
      return false;
    }
  };

  const reopenModally = () => {
    const drawer = document.querySelector('cart-drawer-component');
    const dialog = drawer?.querySelector('.cart-drawer__dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    // A morph can preserve the `open` attribute without restoring the dialog
    // to the browser's top layer, which leaves a "floating header over PDP"
    // state. In that case, force a real modal reopen.
    if (dialog.open && isModalDialog(dialog)) return;

    dialog.style.setProperty('--dialog-drawer-opening-animation', 'none');
    dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');

    if (dialog.open && !isModalDialog(dialog)) {
      dialog.removeAttribute('open');
    }

    try {
      dialog.showModal();
    } catch {
      // Fallback to the component API only if direct modal restore fails.
      if (drawer && typeof drawer.open === 'function') {
        try {
          drawer.open();
        } catch {
          dialog.setAttribute('open', '');
        }
      } else {
        dialog.setAttribute('open', '');
      }
    }

    requestAnimationFrame(() => {
      dialog.style.removeProperty('--dialog-drawer-opening-animation');
      dialog.style.removeProperty('--dialog-drawer-closing-animation');
    });
  };

  // Let the cart morph finish first. In the common case the dialog stays modal
  // and this becomes a no-op; if it drops out of the top layer, we restore it
  // once without watching the whole subtree and causing extra visual churn.
  queueMicrotask(reopenModally);
  requestAnimationFrame(reopenModally);
}

async function quickAddToCart(variantId) {
  const sections = getCartSectionIds();
  const response = await fetch(Theme.routes.cart_add_url, {
    ...fetchConfig('json', {
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sections.join(','),
      }),
    }),
  });
  const data = await response.json();

  if (data.status || data.errors) {
    document.dispatchEvent(
      new CartErrorEvent(QUICK_ADD_SOURCE_ID, data.message || 'Cart error', data.description, data.errors)
    );
    showCartDrawerInlineError(data.description || data.message);
    return false;
  }

  preserveDrawerOpenAcrossMorph();
  clearCartDrawerInlineError();
  // POST /cart/add.js returns only the added line (no top-level item_count), so
  // increment by 1 via the product-form source instead of an absolute set to undefined.
  document.dispatchEvent(
    new CartUpdateEvent(data, QUICK_ADD_SOURCE_ID, {
      source: 'product-form-component',
      itemCount: 1,
      sections: data.sections,
    })
  );
  return true;
}

// Simple + Agregar (single-variant)
document.addEventListener('click', async (event) => {
  const btn = event.target.closest('[data-cart-drawer-add]');
  if (!(btn instanceof HTMLButtonElement)) return;

  const variantId = Number(btn.dataset.variantId);
  if (!variantId || btn.disabled) return;

  event.preventDefault();
  btn.disabled = true;
  const originalText = btn.textContent;
  btn.textContent = 'Agregando…';

  try {
    const ok = await quickAddToCart(variantId);
    if (!ok) {
      btn.textContent = originalText;
      btn.disabled = false;
    }
    // On success, morph will re-render the recs.
  } catch (error) {
    console.error(error);
    showCartDrawerInlineError();
    btn.textContent = originalText;
    btn.disabled = false;
  }
});

/* ======================================================
   Variant picker bottom sheet
   ====================================================== */
function swatchColor(handle) {
  const map = {
    'rojo-arcilla': '#b3575a', 'nochebuena': '#aa1829', 'melon': '#f4c1a1',
    'corazon-de-melon': '#f4c1a1', 'durazno': '#edb7a5', 'coral': '#e89980',
    'rosa': '#f2c8d0', 'lavanda': '#c9b8de', 'lila': '#c9b8de',
    'morado': '#8a6ebf', 'azul-marino': '#2c3e50', 'marino': '#2c3e50',
    'azul-cielo': '#c7d9ea', 'cielo': '#c7d9ea', 'azul': '#aec6e3',
    'eucalipto': '#a9b0ae', 'menta': '#c8e2cf', 'verde': '#c4d9c6',
    'oliva': '#a9b38a', 'amarillo': '#f3e3a8', 'mostaza': '#d4a93a',
    'gris-perla': '#d8dde3', 'gris': '#c8cdd2', 'plomo': '#7a838c',
    'blanco-nube': '#ffffff', 'nube': '#ffffff', 'blanco': '#ffffff',
    'white': '#ffffff', 'crema': '#f4ebdb', 'beige': '#e6d9c4',
    'arena': '#d9c7a8', 'cafe': '#8b6947', 'negro': '#1a1a1a', 'black': '#1a1a1a',
  };
  for (const key in map) if (handle.includes(key)) return map[key];
  return '#d8dde3';
}

function slugify(s) {
  return (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function formatMoney(cents) {
  const formatted = (cents / 100).toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `$ ${formatted}`;
}

function escapeHtml(s) {
  return String(s).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
  );
}

(() => {
  const sheet = document.querySelector('[data-cd-sheet]');
  if (!(sheet instanceof HTMLDialogElement)) return;

  const state = { variants: [], colorIdx: -1, sizeIdx: -1, selectedColor: null, selectedSize: null, card: null };
  const els = {
    thumb: sheet.querySelector('[data-cd-sheet-thumb]'),
    title: sheet.querySelector('[data-cd-sheet-title]'),
    price: sheet.querySelector('[data-cd-sheet-price]'),
    details: sheet.querySelector('[data-cd-sheet-details]'),
    colorWrap: sheet.querySelector('[data-cd-sheet-color-wrap]'),
    colorLabel: sheet.querySelector('[data-cd-sheet-color-label]'),
    colorValue: sheet.querySelector('[data-cd-sheet-color-value]'),
    colors: sheet.querySelector('[data-cd-sheet-colors]'),
    sizeWrap: sheet.querySelector('[data-cd-sheet-size-wrap]'),
    sizeLabel: sheet.querySelector('[data-cd-sheet-size-label]'),
    sizeValue: sheet.querySelector('[data-cd-sheet-size-value]'),
    sizes: sheet.querySelector('[data-cd-sheet-sizes]'),
    stockText: sheet.querySelector('[data-cd-sheet-stock-text]'),
    stock: sheet.querySelector('[data-cd-sheet-stock]'),
    atc: sheet.querySelector('[data-cd-sheet-atc]'),
    atcLabel: sheet.querySelector('[data-cd-sheet-atc-label]'),
    atcPrice: sheet.querySelector('[data-cd-sheet-atc-price]'),
  };

  sheet.querySelector('[data-cd-sheet-close]')?.addEventListener('click', () => sheet.close());
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) sheet.close();
  });

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-cd-sheet-open]');
    if (!btn) return;
    event.preventDefault();
    const card = btn.closest('[data-product-url]');
    if (card) openSheet(card);
  });

  function openSheet(card) {
    state.card = card;
    const variantsEl = card.querySelector('[data-cart-drawer-variants]');
    try {
      state.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    } catch {
      state.variants = [];
    }
    state.colorIdx = Number(card.dataset.colorIndex);
    state.sizeIdx = Number(card.dataset.sizeIndex);

    els.title.textContent = card.dataset.productTitle || '';
    els.details.href = card.dataset.productUrl || '#';
    els.thumb.src = card.dataset.thumbnail || '';
    els.thumb.alt = card.dataset.productTitle || '';

    renderOptions(card);
    const firstAvailable = state.variants.find((v) => v.available) || state.variants[0];
    if (firstAvailable) {
      state.selectedColor = state.colorIdx >= 0 ? firstAvailable.options[state.colorIdx] : null;
      state.selectedSize = state.sizeIdx >= 0 ? firstAvailable.options[state.sizeIdx] : null;
    }
    updateSelection();

    if (typeof sheet.showModal === 'function') sheet.showModal();
    else sheet.setAttribute('open', '');
  }

  function renderOptions(card) {
    if (state.colorIdx >= 0) {
      els.colorWrap.hidden = false;
      els.colorLabel.textContent = (card.dataset.colorName || 'COLOR').toUpperCase();
      const colors = [...new Set(state.variants.map((v) => v.options[state.colorIdx]))];
      els.colors.innerHTML = colors
        .map((c) => {
          const handle = slugify(c);
          const cssColor = swatchColor(handle);
          return `<button type="button" class="cd-sheet__swatch" data-color="${escapeHtml(c)}" title="${escapeHtml(c)}" style="--swatch-color: ${cssColor};"></button>`;
        })
        .join('');
      els.colors.querySelectorAll('.cd-sheet__swatch').forEach((b) => {
        b.addEventListener('click', (e) => {
          e.preventDefault();
          state.selectedColor = b.dataset.color;
          updateSelection();
        });
      });
    } else {
      els.colorWrap.hidden = true;
    }

    if (state.sizeIdx >= 0) {
      els.sizeWrap.hidden = false;
      els.sizeLabel.textContent = (card.dataset.sizeName || 'TALLA').toUpperCase();
      const sizes = [...new Set(state.variants.map((v) => v.options[state.sizeIdx]))];
      els.sizes.innerHTML = sizes
        .map((s) => `<button type="button" class="cd-sheet__size" data-size="${escapeHtml(s)}">${escapeHtml(s)}</button>`)
        .join('');
      els.sizes.querySelectorAll('.cd-sheet__size').forEach((b) => {
        b.addEventListener('click', (e) => {
          e.preventDefault();
          if (b.classList.contains('is-unavailable')) return;
          state.selectedSize = b.dataset.size;
          updateSelection();
        });
      });
    } else {
      els.sizeWrap.hidden = true;
    }
  }

  function findVariant() {
    if (!state.variants.length) return null;
    return state.variants.find((v) => {
      const c = state.colorIdx < 0 || v.options[state.colorIdx] === state.selectedColor;
      const s = state.sizeIdx < 0 || v.options[state.sizeIdx] === state.selectedSize;
      return c && s;
    });
  }

  function updateSelection() {
    const variant = findVariant();

    els.colors?.querySelectorAll('.cd-sheet__swatch').forEach((b) => {
      b.classList.toggle('is-selected', b.dataset.color === state.selectedColor);
    });
    if (els.colorValue) els.colorValue.textContent = state.selectedColor || '';

    els.sizes?.querySelectorAll('.cd-sheet__size').forEach((b) => {
      const s = b.dataset.size;
      const match = state.variants.find((v) => {
        const c = state.colorIdx >= 0 ? v.options[state.colorIdx] === state.selectedColor : true;
        return c && v.options[state.sizeIdx] === s;
      });
      const unavailable = !match || !match.available;
      b.classList.toggle('is-unavailable', unavailable);
      b.classList.toggle('is-selected', s === state.selectedSize && !unavailable);
      b.disabled = unavailable;
    });
    if (els.sizeValue) els.sizeValue.textContent = state.selectedSize || '';

    if (variant) {
      els.price.textContent = formatMoney(variant.price);
      els.atcPrice.textContent = formatMoney(variant.price);
    }

    if (state.colorIdx >= 0 && state.selectedColor) {
      const colorVariant = state.variants.find(
        (v) => v.options[state.colorIdx] === state.selectedColor && v.featured_image
      );
      if (colorVariant?.featured_image?.src) {
        const src = colorVariant.featured_image.src;
        els.thumb.src = src + (src.includes('?') ? '&' : '?') + 'width=200';
      }
    }

    const readyToAdd =
      !!(variant && variant.available &&
        (state.colorIdx < 0 || state.selectedColor) &&
        (state.sizeIdx < 0 || state.selectedSize));
    els.atc.disabled = !readyToAdd;

    if (!variant || !variant.available) {
      els.stock.classList.add('is-out');
      els.stockText.textContent = 'Agotado';
    } else {
      els.stock.classList.remove('is-out');
      els.stockText.textContent = 'En stock';
    }

    els.atc.onclick = async () => {
      if (!variant || !variant.available) return;
      els.atc.disabled = true;
      els.atcLabel.textContent = 'Agregando…';
      try {
        const ok = await quickAddToCart(variant.id);
        if (ok) {
          els.atcLabel.textContent = 'Agregado';
          setTimeout(() => {
            sheet.close();
            els.atcLabel.textContent = 'Agregar al carrito';
          }, 700);
        } else {
          els.atcLabel.textContent = 'Agregar al carrito';
          els.atc.disabled = false;
        }
      } catch {
        showCartDrawerInlineError();
        els.atcLabel.textContent = 'Agregar al carrito';
        els.atc.disabled = false;
      }
    };
  }
})();
